import { Alert, modal, Text } from 'bold-ui'
import { confirm } from 'components/modals/confirm'
import { ModalBody } from 'components/modals/ModalBody'
import { FormApi } from 'final-form'
import {
  useEsquemaCalendarioVacinalQuery,
  useFaixaEtariaVacinacaoQuery,
  useLoteHasCadastroQuery,
  useRegistrosAplicadosVacinacaoQuery,
  useRegistrosDoseVacinacaoLazyQuery,
} from 'graphql/hooks.generated'
import { GrupoAlvoVacinacaoEnum, Imunobiologico, RegistroAplicado } from 'graphql/types.generated'
import { groupBy } from 'lodash'
import React, { Fragment, useMemo, useState } from 'react'
import { calculateAge } from 'util/date/calculateAge'
import { v4 as uuidv4 } from 'uuid'
import {
  convertEsquemaCalendarioVacinalQueryToProps,
  updateDosesCalendarioVacinalProps,
  updateDosesOutrosImunosProps,
} from 'view/atendimentos/atendimento-vacinacao/condicoes/converter'
import {
  AtendimentoVacinacaoState,
  CondicoesFormModel,
  meta,
  RegistroVacinacaoFormModel,
  StatusDoseEnum,
} from 'view/atendimentos/atendimento-vacinacao/model'
import { SubModalVacinacaoOptions, VacinacaoModal } from 'view/atendimentos/atendimento-vacinacao/VacinacaoModal'
import {
  checkErrors,
  submitImunobiologicoValidate,
  ValidateRegistroVacinaError,
} from 'view/atendimentos/atendimento-vacinacao/validator'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import { VacinacaoPanel } from '../components/vacinacao/VacinacaoPanel'
import { EstadoBotaoCalendario } from './VacinacaoView'

interface VacinacaoCalendarioViewProps {
  cidadao: CidadaoAtendimento
  condicoesVacinacao: CondicoesFormModel
  dataAtendimento: Instant
  estadoBotaoCalendario: EstadoBotaoCalendario
  grupoAlvoCidadao: GrupoAlvoVacinacaoEnum
  prontuarioId: string
  registros: RegistroVacinacaoFormModel[]
  formVacinacao: FormApi<AtendimentoVacinacaoState>
  mostrarCalendarioCompleto: boolean
  setMostrarCalendarioCompleto: (value: boolean) => void
}

export interface RegistrosAplicadosDto {
  imunobiologicoId: ID
  imunobiologicoSigla: string
  imunobiologicoNome: string
  imunobiologicoNomeFiltro: string
  doseId: ID
  doseNomeApresentacao: string
  doseNome: string
  doseSigla: string
  dataAprazamento: LocalDate
  dataAplicacao: LocalDate
  dataRegistroAprazamento: Instant
  quantidadeAplicacoes: number
}

export interface InfoCardModalVacinacao {
  imunobiologico: Imunobiologico
  doseId: ID
  doseNome: string
  idadeRecomendada: string
  registradoAtendimento: boolean
  status: StatusDoseEnum
}

export interface RegistroVacinacaoFormInput {
  formValues: RegistroVacinacaoFormModel
  onSuccess(): void
}

export function VacinacaoCalendarioView(props: VacinacaoCalendarioViewProps) {
  const {
    cidadao,
    dataAtendimento,
    condicoesVacinacao,
    estadoBotaoCalendario,
    grupoAlvoCidadao,
    prontuarioId,
    formVacinacao,
    mostrarCalendarioCompleto,
    setMostrarCalendarioCompleto,
  } = props

  const [isVacinacaoModalOpen, setIsVacinacaoModalOpen] = useState(false)
  const [
    registrosDoseLazyQuery,
    { data: registrosDoseVacinacaoQueryResult, loading },
  ] = useRegistrosDoseVacinacaoLazyQuery()
  const [infoCardModal, setInfoCardModal] = useState<InfoCardModalVacinacao>(null)
  const [registroEmEdicao, setRegistroEmEdicao] = useState<RegistroVacinacaoFormModel>(null)
  const { refetch: refetchFaixaEtaria } = useFaixaEtariaVacinacaoQuery({ skip: true })
  const { refetch: refetchLoteHasCadastro } = useLoteHasCadastroQuery({ skip: true })

  const { data } = useEsquemaCalendarioVacinalQuery({
    variables: {
      input: {
        sexo: cidadao.sexo,
        grupoAlvo: grupoAlvoCidadao,
        gestante: condicoesVacinacao.gestante,
        dataNascimento: cidadao.dataNascimento,
        dataAtendimento: dataAtendimento,
      },
    },
    fetchPolicy: 'network-only',
  })

  const {
    data: { registrosAplicadosVacinacao },
  } = useRegistrosAplicadosVacinacaoQuery({
    variables: {
      input: {
        prontuarioId: prontuarioId,
        cidadaoId: cidadao.id,
        cpf: cidadao.cpf,
      },
    },
  })

  return useMemo(() => {
    const handleOnDoseClicked = (info: InfoCardModalVacinacao) => {
      setInfoCardModal(info)
      setIsVacinacaoModalOpen(true)
    }

    const handleVacinacaoModalClose = () => {
      setInfoCardModal(null)
      setRegistroEmEdicao(null)
      setIsVacinacaoModalOpen(false)
    }

    const handleOnSubmit = (formValues: RegistroVacinacaoFormModel) => {
      if (formValues.isComunicanteHanseniase) {
        formVacinacao.change(
          meta.condicoesVacinacao.comunicanteHanseniase.absolutePath(),
          formValues.isComunicanteHanseniase
        )
      }

      if (formValues.idUnicoRegistro) {
        const registrosAtualizados = props.registros.filter(
          (item) => item.idUnicoRegistro !== formValues.idUnicoRegistro
        )
        formVacinacao.change(meta.registroVacinacao.absolutePath(), [...registrosAtualizados, formValues])
        setRegistroEmEdicao(null)
      } else {
        setInfoCardModal({ ...infoCardModal, registradoAtendimento: true })
        formVacinacao.change(meta.registroVacinacao.absolutePath(), [
          ...props.registros,
          { ...formValues, idUnicoRegistro: uuidv4() },
        ])
      }
    }

    const validateModalOnSubmit = async (input: RegistroVacinacaoFormInput): Promise<void> => {
      const handleSubmit = () => {
        handleOnSubmit(input.formValues)
        input.onSuccess()
      }

      const error = await submitImunobiologicoValidate({
        ...input.formValues,
        cidadao: cidadao,
        imunobiologicoId: input.formValues.imunobiologicoId || input.formValues.outrosImunosImunobiologico.id,
        dataAtendimento,
        refetchFaixaEtaria,
        loteDataValidade: input.formValues.loteImunobiologico?.validade || input.formValues.loteDataValidade,
        doseId: input.formValues.doseId || input.formValues.outrosImunosDose.id,
        registrosAplicados: registrosAplicadosVacinacao as RegistroAplicado[],
        registrosAplicadosAtendimento: props.registros,
        fabricanteNome: input.formValues.loteFabricante?.nome,
        handleOnSubmit: handleSubmit,
        refetchLoteHasCadastro,
      })

      if (error.impeditivos.length > 0 || error.naoImpeditivos.length > 0) {
        checkErrors(error)
      } else {
        handleSubmit()
      }
    }

    const handleOnDelete = (idUnicoRegistro: ID, tipoModal: SubModalVacinacaoOptions) => {
      const labelTipoRegistro =
        tipoModal === 'APLICACAO' ? 'a aplicação' : tipoModal === 'ANTERIOR' ? 'o registro anterior' : 'o aprazamento'
      return confirm({
        title: `Deseja excluir ${labelTipoRegistro}?`,
        type: 'danger',
        confirmLabel: 'Excluir',
        onConfirm: () => {
          const registrosAtualizados = props.registros.filter((item) => item.idUnicoRegistro !== idUnicoRegistro)
          formVacinacao.change(meta.registroVacinacao.absolutePath(), registrosAtualizados)
          handleVacinacaoModalClose()
        },
        depthLevel: 5,
      })()
    }
    const calendario = groupBy(data.esquemaCalendarioVacinal, 'imunobiologico.id')

    const calendarioVacinalProps = convertEsquemaCalendarioVacinalQueryToProps(
      data,
      cidadao,
      dataAtendimento,
      prontuarioId,
      calendario,
      registrosAplicadosVacinacao
    )

    const clonedDoses = JSON.parse(JSON.stringify(calendarioVacinalProps))

    const calendarioDosesProps = updateDosesCalendarioVacinalProps(clonedDoses.abaCalendario, props.registros)
    const outrasDosesProps = updateDosesOutrosImunosProps(
      clonedDoses.abaOutros,
      props.registros,
      cidadao,
      prontuarioId,
      calendario
    )

    const idadeAnos = calculateAge(cidadao?.dataNascimento, dataAtendimento).years

    return (
      <>
        <VacinacaoModal
          handleModalClose={handleVacinacaoModalClose}
          registrosDoseQueryResult={registrosDoseVacinacaoQueryResult}
          modalOpen={isVacinacaoModalOpen}
          handleOnSubmit={validateModalOnSubmit}
          setIsVacinacaoModalOpen={setIsVacinacaoModalOpen}
          loading={loading}
          infoCardModal={infoCardModal}
          registrosModificados={props.registros}
          handleOnDelete={handleOnDelete}
          setRegistroEmEdicao={setRegistroEmEdicao}
          registroEmEdicao={registroEmEdicao}
          condicoesVacinacao={condicoesVacinacao}
          dataAtendimento={dataAtendimento}
          dataNascimento={cidadao.dataNascimento}
          registrosAplicados={registrosAplicadosVacinacao as RegistroAplicado[]}
        />

        {estadoBotaoCalendario === 'CALENDARIO_VACINAL' ? (
          <Fragment>
            {idadeAnos >= 18 && (
              <Alert inline type='info' style={{ marginBottom: '1rem' }}>
                O registro da vacina COVID-19 está disponível exclusivamente para atendimento à situação nacional de
                Emergência de Saúde Pública. É obrigatória a identificação do cidadão através do CPF ou CNS.
              </Alert>
            )}
            <VacinacaoPanel
              imunobiologicos={calendarioDosesProps}
              handleOnDoseClicked={handleOnDoseClicked}
              registrosDoseLazyQuery={registrosDoseLazyQuery}
              mostrarCalendarioCompleto={mostrarCalendarioCompleto}
              setMostrarCalendarioCompleto={setMostrarCalendarioCompleto}
            />
          </Fragment>
        ) : (
          <VacinacaoPanel
            imunobiologicos={outrasDosesProps}
            outrosImunobiologicos
            handleOnDoseClicked={handleOnDoseClicked}
            registrosDoseLazyQuery={registrosDoseLazyQuery}
            handleOnSubmit={validateModalOnSubmit}
            dataAtendimento={dataAtendimento}
            dataNascimento={cidadao.dataNascimento}
            sexo={cidadao.sexo}
            gestante={condicoesVacinacao.gestante}
          />
        )}
      </>
    )
  }, [
    data,
    cidadao,
    dataAtendimento,
    prontuarioId,
    registrosAplicadosVacinacao,
    props.registros,
    registrosDoseVacinacaoQueryResult,
    isVacinacaoModalOpen,
    loading,
    infoCardModal,
    registroEmEdicao,
    condicoesVacinacao,
    estadoBotaoCalendario,
    registrosDoseLazyQuery,
    mostrarCalendarioCompleto,
    setMostrarCalendarioCompleto,
    formVacinacao,
    refetchFaixaEtaria,
    refetchLoteHasCadastro,
  ])
}

export const createVacinacaoErrorModal = (error: ValidateRegistroVacinaError, submitModal?: () => void) => {
  modal({
    size: 'small',
    render: () => (
      <ModalBody title={error.title} icon={error.icon} type={error.type}>
        <Text dangerouslySetInnerHTML={{ __html: error.subTitle }} />
      </ModalBody>
    ),
    actions: submitModal
      ? [{ label: 'Não' }, { label: 'Sim', kind: 'primary', onClick: () => submitModal() }]
      : error.actions,
    depthLevel: 5,
    closeOnBackdropClick: false,
  })()
}
